import React, { useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import 'semantic-ui-css/components/modal.min.css';
import { Button, Form, Icon, Modal, Table } from 'semantic-ui-react';

export const BANK_PAID_EDIT_LOCKED_MSG = 'Банкаар төлсөн захиалгыг засах боломжгүй';

type EditLine = {
  orderItemId: number;
  productId?: number;
  productName: string;
  price: number;
  originalCount: number;
  newCount: number;
  removed: boolean;
};

type Props = {
  open: boolean;
  orderId: number | string;
  deliveries: any[];
  onClose: () => void;
  onSaved: () => void;
};

function getAdminModalMountNode(): HTMLElement | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.getElementById('admin-modal-mount') ?? document.body;
}

function buildLines(deliveries: any[]): EditLine[] {
  const lines: EditLine[] = [];
  for (const delivery of deliveries || []) {
    for (const item of delivery.items || []) {
      if (String(item.status || '').toUpperCase() === 'ARCHIVED') continue;
      const count = Number(item.count) || 0;
      if (count <= 0) continue;
      lines.push({
        orderItemId: Number(item.id),
        productId: item.product?.id,
        productName: item.product?.name || 'Бараа',
        price: Number(item.price) || 0,
        originalCount: count,
        newCount: count,
        removed: false,
      });
    }
  }
  return lines;
}

function OrderEditModal({ open, orderId, deliveries, onClose, onSaved }: Props) {
  const [lines, setLines] = useState<EditLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open) {
      setLines(buildLines(deliveries));
      setErrorMsg('');
      setSaving(false);
    }
  }, [open, deliveries]);

  const remainingActive = useMemo(
    () => lines.filter((l) => !l.removed && l.newCount > 0).length,
    [lines]
  );

  const hasChanges = useMemo(
    () => lines.some((l) => l.removed || l.newCount < l.originalCount),
    [lines]
  );

  const setCount = (orderItemId: number, next: number) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.orderItemId !== orderItemId || l.removed) return l;
        const clamped = Math.max(1, Math.min(l.originalCount, Math.floor(next) || 1));
        return { ...l, newCount: clamped };
      })
    );
  };

  const removeLine = (orderItemId: number) => {
    if (remainingActive <= 1) {
      setErrorMsg('Сүүлийн барааг хасах боломжгүй');
      return;
    }
    setErrorMsg('');
    setLines((prev) =>
      prev.map((l) =>
        l.orderItemId === orderItemId ? { ...l, removed: true, newCount: 0 } : l
      )
    );
  };

  const undoRemove = (orderItemId: number) => {
    setErrorMsg('');
    setLines((prev) =>
      prev.map((l) =>
        l.orderItemId === orderItemId
          ? { ...l, removed: false, newCount: l.originalCount }
          : l
      )
    );
  };

  const save = () => {
    if (!hasChanges) {
      setErrorMsg('Өөрчлөлт байхгүй');
      return;
    }
    if (remainingActive < 1) {
      setErrorMsg('Сүүлийн барааг хасах боломжгүй');
      return;
    }

    const items = lines
      .filter((l) => l.removed || l.newCount < l.originalCount)
      .map((l) => ({
        orderItemId: l.orderItemId,
        newCount: l.removed ? 0 : l.newCount,
      }));

    const token = localStorage.getItem('adminToken');
    if (!token || token === 'undefined') {
      setErrorMsg('Та дахин нэвтэрнэ үү');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    fetch(process.env.REACT_APP_API_URL + 'mongol/order/edit/' + orderId, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then(async (res) => {
        setSaving(false);
        if (res.ok) {
          onSaved();
          onClose();
          return;
        }
        let msg = 'Засвар хадгалахад алдаа гарлаа';
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch (_) {
          /* ignore */
        }
        setErrorMsg(msg);
      })
      .catch(() => {
        setSaving(false);
        setErrorMsg('Сүлжээний алдаа');
      });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      closeIcon
      mountNode={getAdminModalMountNode()}
      data-testid="order-edit-modal"
    >
      <Modal.Header>Захиалга засварлах #{orderId}</Modal.Header>
      <Modal.Content>
        <p>Зөвхөн бараа хасах эсвэл тоо багасгах боломжтой. Сүүлийн барааг хасах боломжгүй.</p>
        {errorMsg ? <div className="error-msg" data-testid="order-edit-error">{errorMsg}</div> : null}
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Бараа</Table.HeaderCell>
              <Table.HeaderCell>Үнэ</Table.HeaderCell>
              <Table.HeaderCell>Хуучин</Table.HeaderCell>
              <Table.HeaderCell>Шинэ тоо</Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {lines.map((line) => (
              <Table.Row
                key={line.orderItemId}
                data-testid={`order-edit-row-${line.orderItemId}`}
                style={line.removed ? { opacity: 0.5 } : undefined}
              >
                <Table.Cell>{line.productName}</Table.Cell>
                <Table.Cell>
                  <NumberFormat
                    displayType="text"
                    value={line.price}
                    suffix="₮"
                    thousandSeparator={true}
                  />
                </Table.Cell>
                <Table.Cell>{line.originalCount}</Table.Cell>
                <Table.Cell>
                  {line.removed ? (
                    <span>0 (хассан)</span>
                  ) : (
                    <div data-testid={`order-edit-count-${line.orderItemId}`}>
                      <Form.Input
                        type="number"
                        min={1}
                        max={line.originalCount}
                        value={line.newCount}
                        onChange={(e) => setCount(line.orderItemId, Number(e.target.value))}
                      />
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {line.removed ? (
                    <Button
                      type="button"
                      size="mini"
                      data-testid={`order-edit-undo-${line.orderItemId}`}
                      onClick={() => undoRemove(line.orderItemId)}
                    >
                      Буцаах
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="mini"
                      color="red"
                      disabled={remainingActive <= 1}
                      data-testid={`order-edit-remove-${line.orderItemId}`}
                      onClick={() => removeLine(line.orderItemId)}
                    >
                      <Icon name="trash" /> Хасах
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" data-testid="order-edit-cancel" onClick={onClose} disabled={saving}>
          Болих
        </Button>
        <Button
          type="button"
          primary
          data-testid="order-edit-save"
          onClick={save}
          loading={saving}
          disabled={saving || !hasChanges}
        >
          Хадгалах
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default OrderEditModal;
